"use client";

import { forwardRef, useRef } from "react";
import { AnimatedBeam } from "@/components/magicui/animated-beam";
import { cn } from "@/lib/utils";

const CIRCLE_BASE =
  "z-10 flex size-14 sm:size-16 items-center justify-center rounded-full border border-neutral-200 bg-white/95 backdrop-blur-md p-2 shadow-[0_12px_30px_-16px_rgba(14,18,27,0.22)]";

const Circle = forwardRef<
  HTMLDivElement,
  { className?: string; children?: React.ReactNode }
>(function Circle({ className, children }, ref) {
  return (
    <div ref={ref} className={cn(CIRCLE_BASE, className)}>
      {children}
    </div>
  );
});

/**
 * "Built on standards" composition: six certification/standard anchors arranged
 * around a centered DivetIQ mark, with animated beams traveling to the center.
 */
export function StandardsBeamComposition() {
  const containerRef = useRef<HTMLDivElement>(null);

  const centerRef = useRef<HTMLDivElement>(null);

  // Left column anchors (top-to-bottom)
  const leftTopRef = useRef<HTMLDivElement>(null);
  const leftMidRef = useRef<HTMLDivElement>(null);
  const leftBotRef = useRef<HTMLDivElement>(null);

  // Right column anchors (top-to-bottom)
  const rightTopRef = useRef<HTMLDivElement>(null);
  const rightMidRef = useRef<HTMLDivElement>(null);
  const rightBotRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={containerRef}
      className="relative mx-auto flex h-[340px] w-full max-w-2xl items-center justify-center overflow-hidden sm:h-[390px]"
    >
      {/* Subtle radial brand wash so the circles read above the beams */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(closest-side,rgba(62,111,243,0.08),rgba(255,255,255,0)_72%)]"
      />

      {/* Anchor grid */}
      <div className="relative z-10 flex size-full max-w-xl items-center justify-between gap-3 px-10 sm:px-14">
        {/* Left column */}
        <div className="flex h-full flex-col justify-between py-12">
          <Circle ref={leftTopRef}>
            <Badge label="SOC 2" />
          </Circle>
          <Circle ref={leftMidRef}>
            <Badge label="ISO 27001" />
          </Circle>
          <Circle ref={leftBotRef}>
            <Badge label="GDPR" />
          </Circle>
        </div>

        {/* Center logo — blue circled DivetIQ mark, large */}
        <Circle
          ref={centerRef}
          className="size-20 border-brand/40 bg-brand p-0 shadow-[0_24px_60px_-18px_rgba(62,111,243,0.55)] sm:size-24"
        >
          <div className="grid size-full place-items-center">
            <DivetiqMarkLight />
          </div>
        </Circle>

        {/* Right column */}
        <div className="flex h-full flex-col justify-between py-12">
          <Circle ref={rightTopRef}>
            <Badge label="OpenAPI" />
          </Circle>
          <Circle ref={rightMidRef}>
            <Badge label="BPMN" />
          </Circle>
          <Circle ref={rightBotRef}>
            <Badge label="EU SC" />
          </Circle>
        </div>
      </div>

      <AnimatedBeam
        containerRef={containerRef}
        fromRef={leftTopRef}
        toRef={centerRef}
        curvature={-28}
        delay={0.0}
        pathWidth={1.2}
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={leftMidRef}
        toRef={centerRef}
        curvature={0}
        delay={0.4}
        pathWidth={1.2}
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={leftBotRef}
        toRef={centerRef}
        curvature={28}
        delay={0.8}
        pathWidth={1.2}
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={rightTopRef}
        toRef={centerRef}
        curvature={-28}
        delay={0.2}
        pathWidth={1.2}
        reverse
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={rightMidRef}
        toRef={centerRef}
        curvature={0}
        delay={0.6}
        pathWidth={1.2}
        reverse
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={rightBotRef}
        toRef={centerRef}
        curvature={28}
        delay={1.0}
        pathWidth={1.2}
        reverse
      />
    </div>
  );
}

function Badge({ label }: { label: string }) {
  return (
    <span className="text-center text-[10px] font-bold uppercase tracking-tight text-neutral-800 sm:text-[11px]">
      {label}
    </span>
  );
}

/** Logo mark in white-on-brand for use inside the blue centered circle. */
function DivetiqMarkLight() {
  return (
    <svg viewBox="0 0 35 35" width="58%" height="58%" aria-hidden>
      <path
        d="M7.23297 16.5921C7.23297 14.2371 9.14211 12.3279 11.4972 12.3279L18.693 12.3279L22.0807 16.5264L22.0807 8.99654L27.7544 6.9977L27.7544 17.1251L27.7544 23.8572C27.7544 26.2122 25.8452 28.1214 23.4902 28.1214L15.2283 28.1214L11.4972 28.1214C9.14211 28.1214 7.23297 26.2122 7.23297 23.8572L7.23297 16.5921ZM12.1372 16.5264L12.1372 23.877L22.0807 23.877L22.0807 16.5264L12.1372 16.5264Z"
        fill="white"
      />
    </svg>
  );
}
